import { useMemo } from 'react';

import truncate from 'lodash/truncate';

import {
  Area,
  Bar,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  DataPopperRow,
  Line,
  TimeXAxis,
  XAxis,
  YAxis,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';
import type { RechartsChartConfig } from '@cast/types';
import {
  dashesOrNum,
  formatCurrency,
  getCurrencyPrecision,
  toNumOrZero,
} from '@cast/utils';

import { PieChartArtifact } from './PieChartArtifact';

const CHART_HEIGHT = 300;

const TIME_SERIES_TYPES = new Set<RechartsChartConfig['chartType']>([
  'line',
  'area',
  'stacked_area',
]);

export const RechartsArtifact = ({
  config,
}: {
  config: RechartsChartConfig;
}) => {
  const { chartType, title, data, config: chartConfig } = config;
  const { xAxisDataKey, series = [], yAxisLabel } = chartConfig;
  const chartData = useMemo(() => data ?? [], [data]);
  const isTimeSeries = TIME_SERIES_TYPES.has(chartType);

  const normalizedSeries = useMemo(
    () =>
      series.filter((s): s is typeof s & { dataKey: string } =>
        Boolean(s?.dataKey)
      ),
    [series]
  );

  const processedData = useMemo(() => {
    if (!isTimeSeries || !xAxisDataKey) return chartData;
    return chartData.map((row) => ({
      ...row,
      // Backend returns unix milliseconds; TimeXAxis expects unix seconds
      chartTimestampUnix: Number(row[xAxisDataKey]) / 1000,
    }));
  }, [chartData, isTimeSeries, xAxisDataKey]);

  // Sanitize null/undefined values to 0 so bars render as empty rather than throwing.
  // TODO(REP-2007): backend should guarantee all series keys are present in every data row.
  const sanitizedData = useMemo(() => {
    if (chartType !== 'bar' && chartType !== 'stacked_bar')
      return processedData;
    const keys = normalizedSeries.map((s) => s.dataKey);
    return processedData.map((row) => {
      const sanitized: Record<string, unknown> = { ...row };
      for (const key of keys) {
        sanitized[key] = toNumOrZero(sanitized[key]);
      }
      return sanitized;
    });
  }, [processedData, chartType, normalizedSeries]);

  if (!chartData.length) return null;

  if (chartType === 'pie') {
    const dataKey = chartConfig.dataKey ?? series[0]?.dataKey;
    if (!dataKey) return null;
    return (
      <PieChartArtifact
        title={title}
        data={chartData}
        dataKey={dataKey}
        nameKey={
          chartConfig.nameKey ?? series[0]?.nameKey ?? xAxisDataKey ?? 'name'
        }
      />
    );
  }

  if (!normalizedSeries.length) {
    return null;
  }

  if ((chartType === 'bar' || chartType === 'stacked_bar') && !xAxisDataKey) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 8,
        border: '1px solid',
        borderColor: tokens.theme.border.structural.neutral,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {title && (
        <Typography
          variant="P14B"
          sx={{
            display: 'block',
            p: 12,
            pb: 0,
            color: tokens.theme.fg.structural.primary,
          }}
        >
          {title}
        </Typography>
      )}
      <Box height={CHART_HEIGHT} position="relative">
        <ChartContainer<Record<string, unknown>>
          data={sanitizedData}
          sx={{ width: '100%' }}
          margin={{ top: 40, left: 0, right: 0, bottom: 40 }}
        >
          <ChartGrid hideVertical />

          {(chartType === 'bar' || chartType === 'stacked_bar') &&
            normalizedSeries.map((s) => (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                fill={s.color}
                stackId={
                  chartType === 'stacked_bar' ? s.stackId ?? 'stack' : undefined
                }
                tooltipConfig={{ displayName: s.name || s.dataKey }}
              />
            ))}

          {chartType === 'line' &&
            normalizedSeries.map((s) => (
              <Line
                key={s.dataKey}
                dataKey={s.dataKey}
                stroke={s.color}
                tooltipConfig={{ displayName: s.name || s.dataKey }}
              />
            ))}

          {(chartType === 'area' || chartType === 'stacked_area') &&
            normalizedSeries.map((s) => (
              <Area
                key={s.dataKey}
                dataKey={s.dataKey}
                fill={s.color}
                fillOpacity={0.15}
                stackId={
                  chartType === 'stacked_area'
                    ? s.stackId ?? 'stack'
                    : undefined
                }
                tooltipConfig={{ displayName: s.name || s.dataKey }}
              />
            ))}

          {isTimeSeries ? (
            <TimeXAxis unixDataKey="chartTimestampUnix" />
          ) : (
            <XAxis
              dataKey={xAxisDataKey ?? ''}
              tickFormatter={(value: unknown) =>
                truncate(String(value ?? ''), { length: 19 })
              }
            />
          )}

          <YAxis
            unitFormatter={
              yAxisLabel ? (value) => `${value} ${yAxisLabel}` : undefined
            }
          />

          <ChartTooltip<Record<string, unknown>>
            header={
              chartType === 'stacked_bar' && xAxisDataKey
                ? ({ data }) => (
                    <DataPopperRow
                      sx={{
                        typography: 'L10M',
                        color: tokens.theme.fg.structural.secondary,
                      }}
                      left={String(data[xAxisDataKey] ?? '')}
                    />
                  )
                : undefined
            }
            formatValue={(value) =>
              dashesOrNum({
                value,
                mapNumber: (num) =>
                  formatCurrency(num, {
                    maximumFractionDigits: getCurrencyPrecision(num),
                  }),
              })
            }
          />
        </ChartContainer>
      </Box>
    </Box>
  );
};
